import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import PageCard from "PFComponents/page/page_card";
import { fetchCategoryOptions } from "PFCore/services/accounts/account";
import { LocalesData } from "PFTypes";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CategoryType, EditAccountForm, FormErrors } from "../edit_account.types";
import css from "./edit_account_page.module.scss";
import { BaseSection } from "./sections/base_section";
import { CategoriesSection } from "./sections/categories_section";
import { DefaultNotificationSettingsSection } from "./sections/default_notification_settings_section";
import { LanguageSection } from "./sections/language_section";

type EditAccountPageProps = {
  account: EditAccountForm;
  localesData: LocalesData;
  saveAccount: (accountId: number | undefined, data: EditAccountForm) => void;
  saving: boolean;
  formErrors: FormErrors | null;
  accountId: number | undefined;
};

export const EditAccountPage = ({
  account,
  saveAccount,
  saving,
  formErrors,
  accountId,
  localesData
}: EditAccountPageProps): JSX.Element => {
  const { t } = useTranslation(["admin", "translation", "saasAdmin"]);

  const formMethods = useForm<EditAccountForm>({
    defaultValues: {
      ...account,
      languageSettings: {
        allowedLocales: localesData.allowedLocales,
        locale: localesData.locale,
        dateFormatKey: localesData.dateFormatKey,
        twelveHourClock: localesData.twelveHourClock,
        availableCurrencies: localesData.availableCurrencies,
        currency: localesData.currency
      }
    }
  });
  const { handleSubmit } = formMethods;

  const handleSave: SubmitHandler<EditAccountForm> = (formData) => saveAccount(accountId, formData);

  const fetchCategoryValuesForAccount = (type: CategoryType, term: string) =>
    fetchCategoryOptions(type, term, accountId);

  return (
    <div className={css.editAccountPage}>
      {!!accountId && (
        <ButtonLink
          href={`/saas_admin/accounts/${accountId}/destroy`}
          kind="danger"
          rootClassName={css.destroyButton}
        >
          {t("translation:destroy")}
        </ButtonLink>
      )}
      <h1>
        {!accountId
          ? t("saasAdmin:accounts.account.newAccount")
          : t("saasAdmin:accounts.account.editAccount")}
      </h1>
      <PageCard>
        <div className={css.formContainer}>
          <FormProvider {...formMethods}>
            <div className={css.formContainer}>
              <BaseSection formErrors={formErrors} />
              <CategoriesSection fetchCategoryValues={fetchCategoryValuesForAccount} />
              <DefaultNotificationSettingsSection />
              <LanguageSection localesData={localesData} />
            </div>
            <div className={css.buttonsContainer}>
              <ButtonLink kind="secondary" href="/saas_admin/accounts">
                {t("translation:cancel")}
              </ButtonLink>
              <Button onClick={handleSubmit(handleSave)} disabled={saving}>
                {t("translation:save")}
              </Button>
            </div>
          </FormProvider>
        </div>
      </PageCard>
    </div>
  );
};
